import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../services/supabase'
import { useCurrentAdvisor } from './useCurrentAdvisor'

const PAGE_SIZE = 50

export function useDMThread(conversationId) {
  const { advisor } = useCurrentAdvisor()
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const offsetRef = useRef(0)

  const fetchPage = useCallback(async (convId, offset = 0) => {
    setLoading(true)
    const { data } = await supabase
      .from('sfp_dm_messages')
      .select(`
        id, conversation_id, sender_id, body, message_type,
        media_url, media_name, media_size, media_mime,
        reply_to_id, edited_at, deleted_at, created_at,
        sender:advisors!sfp_dm_messages_sender_id_fkey(id, full_name, name, avatar_url),
        reactions:sfp_dm_reactions(id, advisor_id, emoji),
        reads:sfp_dm_reads(advisor_id, read_at)
      `)
      .eq('conversation_id', convId)
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1)

    const page = (data ?? []).reverse()
    if (offset === 0) {
      setMessages(page)
    } else {
      setMessages(prev => [...page, ...prev])
    }
    setHasMore((data ?? []).length === PAGE_SIZE)
    offsetRef.current = offset + (data ?? []).length
    setLoading(false)
    return page
  }, [])

  // Mark all unread messages as read
  const markAllRead = useCallback(async (msgs, advisorId) => {
    const unreadIds = msgs
      .filter(m => m.sender_id !== advisorId && !m.deleted_at)
      .filter(m => !(m.reads ?? []).some(r => r.advisor_id === advisorId))
      .map(m => m.id)

    if (unreadIds.length === 0) return

    const rows = unreadIds.map(message_id => ({ message_id, advisor_id: advisorId }))
    await supabase.from('sfp_dm_reads').upsert(rows, { ignoreDuplicates: true })
  }, [])

  useEffect(() => {
    if (!conversationId || !advisor?.id) return
    offsetRef.current = 0

    fetchPage(conversationId, 0).then(msgs => {
      markAllRead(msgs, advisor.id)
    })

    const ch = supabase
      .channel(`dm-thread-${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_messages',
          filter: `conversation_id=eq.${conversationId}` }, async (payload) => {
        // Re-fetch to get joined sender/reactions/reads
        const { data } = await supabase
          .from('sfp_dm_messages')
          .select(`id, conversation_id, sender_id, body, message_type, media_url, media_name, media_size, media_mime, reply_to_id, edited_at, deleted_at, created_at,
            sender:advisors!sfp_dm_messages_sender_id_fkey(id, full_name, name, avatar_url),
            reactions:sfp_dm_reactions(id, advisor_id, emoji),
            reads:sfp_dm_reads(advisor_id, read_at)`)
          .eq('id', payload.new.id)
          .single()
        if (data) {
          setMessages(prev => [...prev, data])
          offsetRef.current += 1
          if (data.sender_id !== advisor.id) {
            markAllRead([data], advisor.id)
          }
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sfp_dm_messages',
          filter: `conversation_id=eq.${conversationId}` }, (payload) => {
        setMessages(prev => prev.map(m => m.id === payload.new.id ? { ...m, ...payload.new } : m))
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_reactions' }, (payload) => {
        setMessages(prev => prev.map(m => m.id === payload.new.message_id
          ? { ...m, reactions: [...(m.reactions ?? []), payload.new] }
          : m))
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'sfp_dm_reactions' }, (payload) => {
        setMessages(prev => prev.map(m => m.id === payload.old.message_id
          ? { ...m, reactions: (m.reactions ?? []).filter(r => r.id !== payload.old.id) }
          : m))
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_reads' }, (payload) => {
        setMessages(prev => prev.map(m => m.id === payload.new.message_id
          ? { ...m, reads: [...(m.reads ?? []), payload.new] }
          : m))
      })
      .subscribe()

    return () => supabase.removeChannel(ch)
  }, [conversationId, advisor?.id, fetchPage, markAllRead])

  const loadEarlier = useCallback(() => {
    if (!conversationId || loading) return
    fetchPage(conversationId, offsetRef.current)
  }, [conversationId, loading, fetchPage])

  const sendMessage = useCallback(async ({ body, messageType = 'text', mediaUrl, mediaName, mediaSize, mediaMime, replyToId }) => {
    if (!conversationId || !advisor?.id) return null
    const { data, error } = await supabase.from('sfp_dm_messages').insert({
      conversation_id: conversationId,
      sender_id: advisor.id,
      body: body ?? null,
      message_type: messageType,
      media_url: mediaUrl ?? null,
      media_name: mediaName ?? null,
      media_size: mediaSize ?? null,
      media_mime: mediaMime ?? null,
      reply_to_id: replyToId ?? null,
    }).select('id').single()

    if (error) return { error }

    // Update conversation preview
    await supabase.from('sfp_dm_conversations').update({
      last_message_at: new Date().toISOString(),
      last_message_preview: messageType === 'text' ? (body ?? '').slice(0, 100) : `[${messageType}]`,
    }).eq('id', conversationId)

    return { id: data.id }
  }, [conversationId, advisor?.id])

  const editMessage = useCallback(async (messageId, newBody) => {
    await supabase.from('sfp_dm_messages').update({ body: newBody, edited_at: new Date().toISOString() }).eq('id', messageId)
  }, [])

  const deleteMessage = useCallback(async (messageId) => {
    await supabase.from('sfp_dm_messages').update({ deleted_at: new Date().toISOString() }).eq('id', messageId)
    // Clear reactions on soft-delete
    await supabase.from('sfp_dm_reactions').delete().eq('message_id', messageId)
  }, [])

  const toggleReaction = useCallback(async (messageId, emoji) => {
    if (!advisor?.id) return
    const existing = messages.find(m => m.id === messageId)
      ?.reactions?.find(r => r.advisor_id === advisor.id && r.emoji === emoji)

    if (existing) {
      await supabase.from('sfp_dm_reactions').delete().eq('id', existing.id)
    } else {
      await supabase.from('sfp_dm_reactions').insert({ message_id: messageId, advisor_id: advisor.id, emoji })
    }
  }, [advisor?.id, messages])

  return { messages, loading, hasMore, loadEarlier, sendMessage, editMessage, deleteMessage, toggleReaction }
}
